import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const KEY = "evove_exit_seen";

export default function ExitIntent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Never show the popup to someone who has already joined.
    if (sessionStorage.getItem("evove_waitlist_done")) return;
    if (sessionStorage.getItem(KEY)) return;

    const trigger = () => {
      if (sessionStorage.getItem("evove_waitlist_done")) return;
      if (sessionStorage.getItem(KEY)) return;
      setOpen(true);
      sessionStorage.setItem(KEY, "1");
    };
    const onOut = (e) => {
      if (e.clientY <= 0 && window.innerWidth >= 768) trigger();
    };
    const onLeave = () => {
      if (window.innerWidth >= 768) trigger();
    };

    // Mobile: fire when the user has scrolled deep into the page, then
    // scrolls upward by a meaningful amount (a common "leave" signal on touch).
    // We prefer Lenis's normalised scroll stream when available (Lenis is
    // initialised in Hero.jsx and hijacks touch/wheel), and fall back to the
    // raw window scroll event otherwise.
    let lastY = window.scrollY;
    let armed = false;
    let upAccum = 0;
    const handleScroll = (y) => {
      if (window.innerWidth >= 768) return;
      const doc = document.documentElement;
      const maxY = doc.scrollHeight - window.innerHeight;
      const pct = maxY > 0 ? y / maxY : 0;
      if (pct > 0.35) armed = true;
      const dy = y - lastY;
      if (armed) {
        if (dy < 0) {
          upAccum += -dy;
          if (upAccum > 180) trigger();
        } else if (dy > 6) {
          upAccum = 0;
        }
      }
      lastY = y;
    };
    const onWinScroll = () => handleScroll(window.scrollY);
    const onLenisScroll = (e) => handleScroll(e.scroll ?? window.scrollY);

    // Mobile fallback: hide-tab (backgrounding) = strong intent-to-leave signal.
    const onVis = () => {
      if (document.visibilityState === "hidden" && window.innerWidth < 768) trigger();
    };

    // Time-based reminder — this is the guaranteed fallback that fires
    // on every device even if none of the intent signals above are hit.
    // Desktop: 28 seconds. Mobile: 22 seconds.
    const reminderDelay = window.innerWidth < 768 ? 22000 : 28000;
    const reminderT = setTimeout(trigger, reminderDelay);

    let lenisUnsub = null;
    const t = setTimeout(() => {
      document.addEventListener("mouseout", onOut);
      document.addEventListener("mouseleave", onLeave);
      if (window.lenis && typeof window.lenis.on === "function") {
        window.lenis.on("scroll", onLenisScroll);
        lenisUnsub = () => window.lenis?.off?.("scroll", onLenisScroll);
      }
      window.addEventListener("scroll", onWinScroll, { passive: true });
      document.addEventListener("visibilitychange", onVis);
    }, 1500);
    return () => {
      clearTimeout(t);
      clearTimeout(reminderT);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onWinScroll);
      document.removeEventListener("visibilitychange", onVis);
      lenisUnsub?.();
    };
  }, []);

  const join = () => {
    setOpen(false);
    if (window.lenis) window.lenis.scrollTo(0, { duration: 1.1 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-center justify-center px-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{ background: "rgba(20,15,44,0.45)", backdropFilter: "blur(4px)" }}
          onClick={() => setOpen(false)}
          data-testid="exit-intent-modal"
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-md p-10 text-center frame"
            style={{ background: "var(--cream)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="exit-intent-close"
              className="absolute top-4 right-4"
              style={{ color: "var(--paper-dim)" }}
            >
              <X size={18} />
            </button>
            <p
              className="text-[0.6rem] track-sm uppercase"
              style={{ color: "var(--paper-faint)" }}
            >
              Before you go
            </p>
            <h3 className="headline text-2xl md:text-3xl mt-4">
              Don&apos;t miss early access
            </h3>
            <p
              className="mt-4 text-[0.95rem] leading-relaxed"
              style={{ color: "var(--paper-dim)" }}
            >
              Join the evove waitlist for first dibs at launch and exclusive
              community offers.
            </p>
            <button
              onClick={join}
              className="cta mt-8"
              data-testid="exit-intent-join"
            >
              Join the waitlist
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
